/**
 * Where you were: each tool's last view, the scroll inside it, and where an
 * editor was opened from (navigation handoff §4 "Back to where I was", §5 the
 * origin crumb).
 *
 * - **Last view per tool** (localStorage): switching back to Tables from the
 *   Lilia menu lands on Has problems if that's where you left it. The view is
 *   an address — section, filter and sort all live in it — so remembering the
 *   address remembers them.
 * - **Scroll per address** (sessionStorage): returning to a view puts you back
 *   where you were reading, in this tab.
 * - **Origin per visit** (sessionStorage): the view an editor was opened
 *   from, fixed per history entry so a reload keeps it. Opened from a link,
 *   there is no view behind it, and the origin is plain Documents.
 *
 * Storage can be missing or refuse (private windows, quotas); every access is
 * guarded, and a failure only means forgetting.
 */

export interface RememberedView {
  tool: string;
  /** pathname + search. */
  path: string;
  /** "Documents · Drafts", "Tables · Has problems". */
  label: string;
  at: number;
}

const LAST_VIEWS = "lilia.nav.lastViews.v1";
const LAST_SEEN = "lilia.nav.lastSeen.v1";
const SCROLL = "lilia.nav.scroll.v1";
const ORIGIN = "lilia.nav.origin.v1";

function read<T>(store: () => Storage, key: string, fallback: T): T {
  try {
    const raw = store().getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(store: () => Storage, key: string, value: unknown) {
  try {
    store().setItem(key, JSON.stringify(value));
  } catch {
    /* forgetting is the failure mode */
  }
}

const local = () => window.localStorage;
const session = () => window.sessionStorage;

/** A tool view is showing. Called by every tool's rail. */
export function recordView(v: Omit<RememberedView, "at">) {
  const now = { ...v, at: Date.now() };
  const all = read<Record<string, RememberedView>>(local, LAST_VIEWS, {});
  all[v.tool] = now;
  write(local, LAST_VIEWS, all);
  // The last view seen in this tab, whatever the tool: what an item opened
  // next was opened from.
  write(session, LAST_SEEN, now);
}

/** The tool's last view, or undefined when it has none yet. */
export function lastViewOf(tool: string): RememberedView | undefined {
  return read<Record<string, RememberedView>>(local, LAST_VIEWS, {})[tool];
}

/** The address to open a tool at: its last view, else its root. */
export function toolEntry(tool: string, root: string): string {
  const v = lastViewOf(tool);
  return v?.path ?? root;
}

export function saveScroll(path: string, top: number) {
  const all = read<Record<string, number>>(session, SCROLL, {});
  if (top > 0) all[path] = Math.round(top);
  else delete all[path];
  write(session, SCROLL, all);
}

export function scrollFor(path: string): number {
  return read<Record<string, number>>(session, SCROLL, {})[path] ?? 0;
}

const FALLBACK_ORIGIN = { path: "/documents", label: "Documents" };

/**
 * The view an item was opened from.
 *
 * Keyed by the history entry (the router's location key plus the item), so it
 * is fixed per visit: a reload, or Back and Forward to the same entry, ask
 * again and get the same answer — while opening the same paper later from
 * somewhere else is a new entry, with its own origin.
 */
export function originFor(entryKey: string): { path: string; label: string } {
  const all = read<Record<string, { path: string; label: string }>>(session, ORIGIN, {});
  if (all[entryKey]) return all[entryKey];
  const seen = read<RememberedView | null>(session, LAST_SEEN, null);
  const origin = seen ? { path: seen.path, label: seen.label } : FALLBACK_ORIGIN;
  // Bounded: a long session opens a lot of papers.
  const keys = Object.keys(all);
  if (keys.length > 200) for (const k of keys.slice(0, keys.length - 200)) delete all[k];
  all[entryKey] = origin;
  write(session, ORIGIN, all);
  return origin;
}
